import { combine } from "@turf/turf";
import { SettingsManager } from "@viamap/viamap2-common";
import { WFStoGeoJson } from "src/functions/WFSutils";
import { GenerateGeomUtils } from "src/managers/GenerateGeomUtils";
import * as wkt from "wkt";
import { wfsEndpoints } from "./WFSCollectorEndpoints";

export class WFSCollector {

   static _featureDescriptionCache: { [key: string]: Promise<any> } = {};
   static async getFeatureType(endpoint: string, featureType: string) {
      let endP = wfsEndpoints[endpoint] as wfsEndpointType;
      if (!endP.featureTypes || !(featureType in endP.featureTypes)) {
         throw new Error(`Feature type ${featureType} not found in endpoint ${endpoint}, check your spelling. (Case-sensitive)`);
      }
      let cacheKey = `${endpoint}_${endP.featureTypes?.[featureType].split(":").at(-1) as string}`;
      if (!(cacheKey in WFSCollector._featureDescriptionCache)) {
         let types = await WFSCollector.describeFeatureType(endpoint as any, featureType as any);
         Object.entries(types).forEach(([typeName, props]: [string, any]) => {
            WFSCollector._featureDescriptionCache[`${endpoint}_${typeName}`] = Promise.resolve(props);
         });
         return WFSCollector._featureDescriptionCache[cacheKey];
      }
      return WFSCollector._featureDescriptionCache[cacheKey];
   }

   static async getFeatureInsidePolygon<E extends keyof typeof wfsEndpoints, T extends keyof typeof wfsEndpoints[E]["featureTypes"]>(endpoint: E, featureType: T, geom: GeoJSON.FeatureCollection<GeoJSON.Polygon | GeoJSON.MultiPolygon>, options: { whoami?: string } = {}) {
      const endP = wfsEndpoints[endpoint] as wfsEndpointType;
      const url = new URL(endP.endpoint);
      const params = urlParams[endP.urlParams!]?.() ?? {};

      let type = await WFSCollector.getFeatureType(endpoint, featureType as string);
      let geomName = extractGeomNameAndType(type);
      let FetchParams = new URLSearchParams({
         ...params,
         request: "GetFeature",
         service: "WFS",
         version: "2.0.0",
         typeName: endP.featureTypes![featureType as string]
      });

      if (endP["whoAmI"]) {
         if (options.whoami) {
            FetchParams.append("whoami", options.whoami);
         } else {
            throw new Error("Disclosure required");
         }
      }

      let bodyForm = new URLSearchParams();
      if (endP.fetchMethods === "Post_Xml") {
         bodyForm.append("filter", generatePostFilterXml(geomName, geom));
      } else {
         bodyForm.append("cql_filter", generateCQLFilter(geomName, geom));
      }

      const response = await fetch(url.toString() + "?" + FetchParams.toString(), {
         method: "POST",
         body: bodyForm,
         headers: {
            "Content-Type": "application/x-www-form-urlencoded"
         }
      });
      if (response.status !== 200) {
         let error = wfsErrorHandler(response.status, response.statusText);
         throw new Error(error);
      }
      const text = await XMLResponseToUTF8(response);
      let geoJson = WFStoGeoJson(text);
      if ("metadata" in geoJson && geoJson.metadata) {
         geoJson = {
            ...geoJson, metadata: {
               ...geoJson.metadata,
               feature: featureType,
               endpoint: endpoint
            }
         } as any;
      }
      if ("crs" in geoJson && geoJson.crs) {
         let crs = (geoJson.crs as any).split("EPSG::").at(-1);
         if (crs && crs != "4326") {
            geoJson = GenerateGeomUtils.geoJsonCoordsTransformer(geoJson, (coord) => {
               let latLng = GenerateGeomUtils.convertFromUTMtoLatLng2(coord.reverse() as [number, number]).toReversed();
               return latLng as [number, number];
            });
         }
         delete (geoJson as any).crs;
      }
      return geoJson;
   }

   static async getFeatureBBOX<E extends keyof typeof wfsEndpoints, T extends keyof typeof wfsEndpoints[E]["featureTypes"]>(endpoint: E, featureType: T, bbox: [number, number, number, number]) {
      const endP = wfsEndpoints[endpoint] as wfsEndpointType;
      const url = new URL(endP.endpoint);
      url.searchParams.append("request", "GetFeature");
      url.searchParams.append("service", "WFS");
      url.searchParams.append("bbox", bbox.join(",") + ",EPSG:4326");
      if (featureType in endP.featureTypes!) {
         url.searchParams.append("typeName", endP.featureTypes![featureType as string]);
      }
      if (endP.urlParams) {
         const params = urlParams[endP.urlParams]();
         Object.entries(params).forEach(([key, value]) => {
            url.searchParams.append(key, value);
         });
      }

      const response = await fetch(url.toString());
      const text = await XMLResponseToUTF8(response);
      let geoJson = WFStoGeoJson(text);
      if ("crs" in geoJson && geoJson.crs) {
         let crs = (geoJson.crs as any).split("EPSG::").at(-1);
         if (crs && crs != "4326") {
            geoJson = GenerateGeomUtils.geoJsonCoordsTransformer(geoJson, (coord) => {
               let latLng = GenerateGeomUtils.convertFromUTMtoLatLng2(coord.reverse() as [number, number]).toReversed();
               return latLng as [number, number];
            });
         }
         delete (geoJson as any).crs;
      }
      return geoJson;
   }

   static async getCapabilities<T extends keyof typeof wfsEndpoints>(endpoint: T) {
      const endP = wfsEndpoints[endpoint] as wfsEndpointType;
      const url = new URL(endP.endpoint);
      url.searchParams.append("request", "GetCapabilities");
      url.searchParams.append("service", "WFS");
      if (endP.urlParams) {
         const params = urlParams[endP.urlParams]();
         Object.entries(params).forEach(([key, value]) => {
            url.searchParams.append(key, value);
         });
      }
      const response = await fetch(url.toString());
      let xmlText = await XMLResponseToUTF8(response);

      return parseGetCapabilities(xmlText);
   }

   static async describeFeatureType<E extends keyof typeof wfsEndpoints, T extends keyof typeof wfsEndpoints[E]["featureTypes"]>(endpoint: E, featureType: T) {
      const endP = wfsEndpoints[endpoint] as wfsEndpointType;
      const url = new URL(endP.endpoint);
      url.searchParams.append("request", "DescribeFeatureType");
      url.searchParams.append("service", "WFS");
      url.searchParams.append("typeName", endP.featureTypes![featureType as string]);
      if (endP.urlParams) {
         const params = urlParams[endP.urlParams]();
         Object.entries(params).forEach(([key, value]) => {
            url.searchParams.append(key, value);
         });
      }

      const response = await fetch(url.toString());
      const text = await XMLResponseToUTF8(response);
      return await parseTypeDescription(text)
   }
}



export type wfsEndpointType = {
   endpoint: string,
   fetchMethods?: "Get_CQL" | "Post_Xml",
   urlParams?: keyof typeof urlParams | undefined,
   geom?: string,
   corsProxy?: boolean,
   featureTypes?: { [featureName: string]: string }
}

const urlParams = {
   "Datafordeler": () => ({
      username: SettingsManager.getSystemSetting("datafordelerUsername"),
      password: SettingsManager.getSystemSetting("datafordelerPassword")
   }),
} as const;

async function XMLResponseToUTF8(response: Response): Promise<string> {
   const utfDecoder = new TextDecoder("utf-8");
   let arrayBuffer = await response.arrayBuffer();
   let xmlText = utfDecoder.decode(arrayBuffer);
   let xmlDoc = new DOMParser().parseFromString(xmlText, "application/xml");
   let encoding = (xmlDoc as any).xmlEncoding || "UTF-8";
   if (encoding.toUpperCase() === "UTF-8") {
      return xmlText;
   }
   const decoder = new TextDecoder(encoding);
   return decoder.decode(arrayBuffer);
}

// MARK: Utility Functions
function parseGetCapabilities(text: string) {
   const parser = new DOMParser();
   const xmlDoc = parser.parseFromString(text, "text/xml");


   let featureTypes = [...xmlDoc.getElementsByTagName("FeatureType"), ...xmlDoc.getElementsByTagName("wfs:FeatureType")];
   let posibleFeature: { [Title: string]: string } = {}
   for (let i = 0; i < featureTypes.length; i++) {
      let name = featureTypes[i].getElementsByTagName("Name")[0]?.textContent;
      name ??= featureTypes[i].getElementsByTagName("wfs:Name")[0]?.textContent;
      let title = featureTypes[i].getElementsByTagName("Title")[0]?.textContent;
      title ??= featureTypes[i].getElementsByTagName("wfs:Title")[0]?.textContent;
      if (name) {
         posibleFeature[title || name] = name;
      }
   }

   return posibleFeature;
}

/**
 * FeatureCollection to single geometry by unioning all features into one.
 */
function FCToGeometry(collection) {
   if (collection.features.length > 1) {
      return combine(collection)?.features.map((f) => f.geometry)[0];
   }
   return collection.features[0]?.geometry;
}

function generateCQLFilter(geomDesc: { name: string, type: string } | null, geom: any): string {
   if (!geomDesc) {
      throw new Error("Invalid geometry description");
   }
   let geomName = geomDesc.name;
   let geometry = GenerateGeomUtils.geoJsonCoordsTransformer<number>(FCToGeometry(geom), (coord) => {
      let utmCoord = GenerateGeomUtils.convertFromLatLngtoUTM2(coord.reverse() as [number, number]);
      return utmCoord as [number, number];
   });
   return `INTERSECTS(${geomName},${wkt.stringify(geometry)})`;
}

async function parseTypeDescription(typeDescription: string, deepth = 0): Promise<any> {
   if (deepth > 5) {
      throw new Error("Max depth reached when fetching external schemas");
   }
   const parser = new DOMParser();

   // Datafordeler uses external schemas for their WFS types
   const xmlDoc = parser.parseFromString(typeDescription, "text/xml");
   let externalSchemas = xmlDoc.getElementsByTagName("xsd:include")[0];
   let schemaLocation = externalSchemas?.getAttribute("schemaLocation");
   if (schemaLocation) {
      return await fetch(schemaLocation)
         .then(response => response.text())
         .then(schema => {
            return parseTypeDescription(schema, deepth + 1);
         }).then(result => {
            return result;
         });
   }

   let elements: { [key: string]: string } = {};
   let types: { [key: string]: any } = {};

   let schema = xmlDoc.children;
   let children = schema[0].children;

   for (let i = 0; i < children.length; i++) {
      let tagName = children[i].tagName.split(":").pop();
      switch (tagName) {
         case "element":
            let name = children[i].getAttribute("name") ?? "";
            let type = children[i].getAttribute("type")?.split(":").pop() || "";
            elements[name] = type;
            break;
         case "complexType":
            let typeName = children[i].getAttribute("name") ?? "";
            let typeDef = parseComplexType(children[i]);
            types[typeName] = typeDef;
            break;
         case "simpleType":
            let simpleTypeName = children[i].getAttribute("name") ?? "";
            let simpleTypeDef = parseSimpleType(children[i]);
            types[simpleTypeName] = simpleTypeDef;
            break;
         default:
            break;
      }
   }
   let finalTypes = joinTypesWithTypes(types);
   let finalElements = joinElementsWithTypes(elements, finalTypes);
   return finalElements;
}

function parseComplexType(node: Element) {
   let elements = [...node.getElementsByTagName("xsd:element"), ...node.getElementsByTagName("element")];
   let complexType = {};
   for (let i = 0; i < elements.length; i++) {
      let name = elements[i].getAttribute("name")?.split(":").pop();
      let type = elements[i].getAttribute("type")?.split(":").pop();
      if (name == undefined) {
         console.warn("Element name is undefined");
         continue;
      }
      if (type == undefined) {
         let children = elements[i].children;
         let tagName = children[0].tagName.split(":").pop();
         switch (tagName) {
            case "complexType":
               console.warn("Nested complexType found, which is not supported yet.");
               break;
            case "simpleType":
               type = parseSimpleType(children[0]);
               break;
            default:
               break;
         }

      }
      complexType[name!] = type!;
   }
   return complexType;
}

function parseSimpleType(node: Element): string | undefined {
   let restriction = node.getElementsByTagName("xsd:restriction")[0] || node.getElementsByTagName("restriction")[0];
   let base = restriction.getAttribute("base")?.split(":").pop();
   return base;
}

function joinElementsWithTypes(elements: { [key: string]: string }, types: { [key: string]: any }) {
   let typeNames = Object.keys(types);
   let finalElements: { [key: string]: any } = {};
   for (let elementName in elements) {
      let typeName = elements[elementName];
      if (typeNames.includes(typeName)) {
         finalElements[elementName] = types[typeName];
      }
   }
   return finalElements;
}

function joinTypesWithTypes(types: { [key: string]: any }) {
   let typeNames = Object.keys(types);
   let finalTypes: { [key: string]: any } = {};
   for (let typeName in types) {
      let typeDef = types[typeName];
      if (typeof typeDef !== "object") {
         finalTypes[typeName] = parseCommonXSDTypes(typeDef);
         continue;
      }
      let finalTypeDef: { [key: string]: any } = {};
      for (let propName in typeDef) {
         let propTypeName = typeDef[propName];
         if (typeNames.includes(propTypeName)) {
            finalTypeDef[propName] = types[propTypeName];
         } else {
            finalTypeDef[propName] = parseCommonXSDTypes(propTypeName);
         }
      }
      finalTypes[typeName] = finalTypeDef;
   }
   return finalTypes;
}

function extractGeomNameAndType(typeDef: any): { name: string, type: string } | null {
   for (let propName in typeDef) {
      let propType = typeDef[propName];
      if (typeof propType === "string" && propType.startsWith("Geom")) {
         return { name: propName, type: parseGeomType(propType) };
      }
   }
   console.error("No geometry found in type definition:", typeDef);
   return null;
}

function parseGeomType(typeName: string): string {
   if (typeName.includes("Polygon")) {
      return "Polygon";
   } else if (typeName.includes("Point")) {
      return "Point";
   } else if (typeName.includes("LineString")) {
      return "LineString";
   }
   return "unknown";
}

function parseCommonXSDTypes(typeName: string): string {
   switch (typeName) {
      case "string":
         return "string";
      case "anyURI":
         return "string (URI)";
      case "dateTime":
         return "string (dateTime)";
      case "date":
         return "string (date)";
      case "decimal":
      case "double":
      case "float":
      case "short":
      case "integer":
      case "long":
      case "int":
      case "AreaType":
         return "number";
      case "boolean":
         return "boolean";
      case "MultiSurfacePropertyType":
      case "SurfacePropertyType":
         return "Geom (Polygon)";
      case "MultiPointPropertyType":
      case "PointPropertyType":
         return "Geom (Point)";
      case "CurvePropertyType":
      case "MultiCurvePropertyType":
         return "Geom (LineString)";
      case "GeometryPropertyType":
         return "Geom (Unknown)";
      default:
         console.error("Unknown type found in parseCommonXSDTypes:", typeName);
         return "unknown: " + typeName;
   }
}

function wfsErrorHandler(status: number, _statusText: string): string {
   switch (status) {
      case 400:
         return "Bad Request"
      case 401:
         return "Unauthorized"
      case 403:
         return "Forbidden"
      case 404:
         return "Not Found"
      case 500:
         return "Internal Server Error"
      default:
         return "Unknown Error"
   }
}

function generatePostFilterXml(geomDesc: { name: string, type: string } | null, geom) {
   let geometry = GenerateGeomUtils.geoJsonCoordsTransformer<number>(FCToGeometry(geom), (coord) => {
      let utmCoord = GenerateGeomUtils.convertFromLatLngtoUTM2(coord.reverse() as [number, number]);
      return utmCoord as [number, number];
   });

   let filter = `
      <Filter> 
         <Within>
         <PropertyName>${geomDesc?.name || "geom"}</PropertyName>
         ${geometryToGML(geometry)}
         </Within>
      </Filter>
   `.trim();
   return filter;
}

function geometryToGML(geometry: any): string {
   let result = "To be implemented";
   console.log("Generating GML for geometry:", geometry);
   switch (geometry.type) {
      case "Point":
         result = `
            <gml:Point xmlns:gml="http://www.opengis.net/gml" srsName="EPSG:25832">
               <gml:pos>${geometry.coordinates[0]} ${geometry.coordinates[1]}</gml:pos>
            </gml:Point>
         `;
         break;
      case "MultiPoint":
         result = `
            <gml:MultiPoint xmlns:gml="http://www.opengis.net/gml" srsName="EPSG:25832">
               ${geometry.coordinates.map((coord: number[]) => `
                  <gml:pointMember>
                     <gml:Point>
                        <gml:pos>${coord[0]} ${coord[1]}</gml:pos>
                     </gml:Point>
                  </gml:pointMember>
               `).join("")}
            </gml:MultiPoint>
         `;
         break;
      case "LineString":
         result = `
            <gml:LineString xmlns:gml="http://www.opengis.net/gml" srsName="EPSG:25832">
               <gml:posList srsDimension="2">
                  ${geometry.coordinates.map((coord: number[]) => `${coord[0]} ${coord[1]}`).join(" ")}
               </gml:posList>
            </gml:LineString>
         `;
         break;
      case "MultiLineString":
         result = `
            <gml:MultiLineString xmlns:gml="http://www.opengis.net/gml" srsName="EPSG:25832">
               ${geometry.coordinates.map((lineCoords: number[][]) => `
                  <gml:lineStringMember>
                     <gml:LineString>
                        <gml:posList srsDimension="2">
                           ${lineCoords.map((coord: number[]) => `${coord[0]} ${coord[1]}`).join(" ")}
                        </gml:posList>
                     </gml:LineString>
                  </gml:lineStringMember>
               `).join("")}
            </gml:MultiLineString>
         `;
         break;
      case "Polygon":
         result = `
            <gml:Polygon xmlns:gml="http://www.opengis.net/gml" srsName="EPSG:25832">
               <gml:exterior>
                  <gml:LinearRing>
                     <gml:posList srsDimension="2">${geometry.coordinates[0].map((coord: number[]) => `${coord[0]} ${coord[1]}`).join(" ")}</gml:posList>
                  </gml:LinearRing>
               </gml:exterior>
            </gml:Polygon>
         `;
         break;
      case "MultiPolygon":
         result = `
            <gml:MultiPolygon xmlns:gml="http://www.opengis.net/gml" srsName="EPSG:25832">
               ${geometry.coordinates.map((polygonCoords: number[][][]) => `
                  <gml:polygonMember>
                     <gml:Polygon>
                        <gml:exterior>
                           <gml:LinearRing>
                              <gml:posList srsDimension="2">${polygonCoords[0].map((coord: number[]) => `${coord[0]},${coord[1]}`).join(" ")}</gml:posList>
                           </gml:LinearRing>
                        </gml:exterior>
                        ${polygonCoords.slice(1).map((interiorRingCoords: number[][]) => `
                              <gml:interior>
                                 <gml:LinearRing>
                                    <gml:posList srsDimension="2">${interiorRingCoords.map((coord: number[]) => `${coord[0]},${coord[1]}`).join(" ")}</gml:posList>
                                 </gml:LinearRing>
                              </gml:interior>
                           `).join("")
            }
                     </gml:Polygon>
                  </gml:polygonMember>
               `).join("")}
            </gml:MultiPolygon>
         `;
         break;
   }
   return result;
}
